import type { EAircraftReportType, EFlightCategory, Point } from '#shared';

export type AirSeverity =
  | 'NEG'
  | 'LGT'
  | 'LGT-MOD'
  | 'MOD'
  | 'MOD-SEV'
  | 'SEV'
  | 'SEV-EXTRM'
  | 'EXTRM';
export type AirSigmet = {
  rawText: string;
  validTimeFrom: string;
  validTimeTo: string;
  lonLatPoints: Point[];
  minFtMsl: string;
  maxFtMsl: string;
  movementDirDegrees: string;
  movementSpeedKt: string;
  hazard: 'CONVECTIVE' | 'IFR' | 'MTN OBSCN' | 'TURB' | 'ICE';
  severity: AirSeverity;
  airsigmetType: 'AIRMET' | 'SIGMET';
};
export type AirMetarQualityControlFlags = {
  auto: boolean;
  autoStation: boolean;
  presentWeatherSensorOff: boolean;
  maintenanceIndicatorOn: boolean;
  lightningSensorOff: boolean;
  freezingRainSensorOff: boolean;
  noSignal: boolean;
  corrected: boolean;
};

export type AirMetar = {
  stationId: string;
  observationTime: string;
  latitude: number;
  longitude: number;
  tempC: number | null;
  dewpointC: number | null;
  windDirDegrees: 'VRB' | number;
  windSpeedKt: number | null;
  visibilityStatuteMi: string | number;
  altimInHg: number | null;
  qualityControlFlags: AirMetarQualityControlFlags | null;
  flightCategory: EFlightCategory;
  metarType: 'METAR' | 'SPECI' | null;
  elevationM?: number | null;
  skyCondition: {
    skyCover: string;
    cloudBaseFtAgl: string;
  }[];
  windGustKt: number | null;
  wxString: string | null;
  precipIn: number | null;
  vertVisFt: number | null;
  seaLevelPressureMb: number | null;
  minTC: number | null;
  corrected: string;
  threeHrPressureTendencyMb: string;
  maxTC: string;
  maxT24hrC: string;
  minT24hrC: string;
  pcp3hrIn: string;
  pcp6hrIn: string;
  pcp24hrIn: string;
  snowIn: string;
  rawData: string;
};

export type AircraftReport = {
  receipt_time: string;
  observation_time: string;
  mid_point_assumed: boolean;
  no_time_stamp: boolean;
  flt_lvl_range: boolean;
  above_ground_level_indicated: boolean;
  no_flt_lvl: boolean;
  bad_location: boolean;
  aircraft_ref: string;
  latitude: number;
  longitude: number;
  altitude_ft_msl: number;

  sky_condition: AircraftReportSkyCondition[];
  turbulence_condition: AircraftReportTurbulenceCondition[];
  icing_condition: AircraftReportIcingCondition[];

  visibility_statute_mi?: number;
  wx_string?: string;
  temp_c?: number;
  wind_dir_degrees?: number;
  wind_speed_kt?: number;
  vert_gust_kt: string;
  report_type: EAircraftReportType;
  raw_text: string;
};

export type AircraftReportSkyCondition = {
  sky_cover: string;
  cloud_base_ft_msl: string;
  cloud_top_ft_msl: string;
};
export type AircraftReportTurbulenceCondition = {
  turbulence_type: string;
  turbulence_intensity:
    | 'NEG'
    | 'SMOOTH'
    | 'LGT'
    | 'LGT-MOD'
    | 'MOD'
    | 'MOD-SEV'
    | 'SEV'
    | 'EXTRM'
    | 'LLWS';
  turbulence_base_ft_msl: string;
  turbulence_top_ft_msl: string;
  turbulence_freq: string;
};
export type AircraftReportIcingCondition = {
  icing_type: string;
  icing_intensity:
    | 'NEG'
    | 'TRACE'
    | 'TRACE-LGT'
    | 'LGT'
    | 'LGT-MOD'
    | 'MOD'
    | 'MOD-SEV'
    | 'SEV'
    | 'OTHER';
  icing_base_ft_msl: string;
  icing_top_ft_msl: string;
};

export interface Sigmet {
  isigmetId: number;
  icaoId: string;
  firId: string;
  firName: string;
  receiptTime: string;
  validTimeFrom: number;
  validTimeTo: number;
  seriesId: string;
  hazard: 'TS' | 'TU' | 'MT' | 'IC' | 'VA' | 'TC';
  qualifier: string;
  base?: number;
  top?: number;
  geom: string;
  coords: Coord[];
  dir?: string;
  spd?: number;
  chng?: string;
  rawSigmet: string;
}

export interface Coord {
  lat: number;
  lon: number;
}

export type Winds = Wind[];
export type Wind = [
  number, // direction
  number, // speed
  number, // longitude
  number, // latitude
];

export type Fhr =
  | '00'
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12';
export type Tref =
  | '00'
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22'
  | '23';
export type YYYY =
  | '2025'
  | '2026'
  | '2027'
  | '2028'
  | '2029'
  | '2030'
  | '2031'
  | '2032'
  | '2033'
  | '2034'
  | '2035';
export type MM =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12';
export type DD =
  | '01'
  | '02'
  | '03'
  | '04'
  | '05'
  | '06'
  | '07'
  | '08'
  | '09'
  | '10'
  | '11'
  | '12'
  | '13'
  | '14'
  | '15'
  | '16'
  | '17'
  | '18'
  | '19'
  | '20'
  | '21'
  | '22'
  | '23'
  | '24'
  | '25'
  | '26'
  | '27'
  | '28'
  | '29'
  | '30'
  | '31';
export type Dref = `${YYYY}${MM}${DD}`;

export interface BaseGrib2Message {
  alternativeRowScanning: number;
  average: number;
  bitMapIndicator: number;
  bitmapPresent: number;
  centre: number;
  cfName: string;
  cfVarName: string;
  dataDate: number;
  dataTime: number;
  derivedForecast: number;
  discipline: number;
  DxInMetres: number;
  DyInMetres: number;
  editionNumber: number;
  forecastTime: number;
  generatingProcessIdentifier: number;
  getNumberOfValues: number;
  gridDefinitionTemplateNumber: number;
  gridType: string;
  indicatorOfUnitForForecastTime: number;
  interpretationOfNumberOfPoints: number;
  iScansNegatively: number;
  isConstant: number;
  jPointsAreConsecutive: number;
  jScansPositively: number;
  kurtosis: number;
  LaDInDegrees: number;
  Latin1InDegrees: number;
  Latin2: number;
  Latin2InDegrees: number;
  latitudeOfFirstGridPointInDegrees: number;
  latitudeOfSouthernPoleInDegrees: number;
  level: number;
  longitudeOfFirstGridPointInDegrees: number;
  longitudeOfSouthernPoleInDegrees: number;
  LoVInDegrees: number;
  maximum: number;
  minimum: number;
  name: string;
  nameOfFirstFixedSurface: string;
  nameOfSecondFixedSurface: string;
  numberOfDataPoints: number;
  numberOfForecastsInEnsemble: number;
  numberOfMissing: number;
  numberOfValues: number;
  NV: number;
  Nx: number;
  Ny: number;
  orientationOfTheGridInDegrees?: number; // For polar stereographic projection
  packingType: string;
  parameterCategory: number;
  parameterName: string;
  parameterNumber: number;
  parameterUnits: string;
  productDefinitionTemplateNumber: number;
  productionStatusOfProcessedData: number;
  scaledValueOfFirstFixedSurface: number;
  scaledValueOfSecondFixedSurface: number;
  scaleFactorOfFirstFixedSurface: number;
  scaleFactorOfSecondFixedSurface: number;
  shapeOfTheEarth: number;
  shortName: string;
  significanceOfReferenceTime: number;
  skewness: number;
  standardDeviation: number;
  stepRange: string;
  stepUnits: string;
  subCentre: number;
  typeOfFirstFixedSurface: number;
  typeOfGeneratingProcess: number;
  typeOfProcessedData: number;
  typeOfSecondFixedSurface: number;
  unitsOfFirstFixedSurface: string;
  unitsOfSecondFixedSurface: string;
  values: number[];
  jDirectionIncrementInDegrees: number;
  iDirectionIncrementInDegrees: number;
  Ni: number;
  Nj: number;
}

export enum EGfsLevel {
  // 'lev_0_m_above_ground' = 'lev_0_m_above_ground', // 0 m above ground
  // 'lev_0C_isotherm' = 'lev_0C_isotherm', // 0C isotherm
  'lev_10_m_above_ground' = 'lev_10_m_above_ground', // 10 m above ground
  'lev_250_mb' = 'lev_250_mb', // 250 mb
  'lev_550_mb' = 'lev_550_mb', // 550 mb
  'lev_850_mb' = 'lev_850_mb', // 850 mb
}
